import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Shield, Lock, Eye, Database, Globe, Mail, ArrowLeft } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Sun, Moon } from 'lucide-react';

const PrivacyPolicy = () => {
  const { theme, toggleTheme } = useTheme();

  return (
    <>
      <Helmet>
        <title>Privacy Policy | ParachPay</title>
        <meta name="description" content="ParachPay Privacy Policy. Learn how we collect, use, and protect your personal information." />
      </Helmet>
      
      <div className="min-h-screen bg-background">
        {/* Simple Header */}
        <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center gap-2">
                <img 
                  src="/images/parachpay-logo.jpg" 
                  alt="ParachPay Logo" 
                  width="40"
                  height="40"
                  className="h-10 w-auto"
                />
                <span className="font-bold text-xl tracking-wide">PARACHPAY</span>
              </Link>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle">
                  {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                </Button>
                <Link to="/">
                  <Button variant="ghost" className="gap-2">
                    <ArrowLeft className="w-4 h-4" />
                    Back
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </header>

        <div className="max-w-4xl mx-auto py-12 px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/10 mb-4">
              <Shield className="w-8 h-8 text-emerald-600" />
            </div>
            <h1 className="font-heading text-4xl font-bold mb-4">Privacy Policy</h1>
            <p className="text-muted-foreground">Last updated: March 2026</p>
          </div>

          {/* Legal Entity Notice */}
          <Card className="mb-8 border-emerald-500/20 bg-emerald-500/5">
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">
                ParachPay is operated by <strong>ASELU Investment Growth Limited</strong> (Nigeria, RC1947196), 
                registered office 67 Siluko Road, Benin City, Nigeria. 
                Contact: <a href="mailto:info@parachpay.com" className="text-emerald-500 hover:underline">info@parachpay.com</a>
              </p>
            </CardContent>
          </Card>

          <div className="space-y-8">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="w-5 h-5 text-emerald-600" />
                  Introduction
                </CardTitle>
              </CardHeader>
              <CardContent className="prose dark:prose-invert max-w-none">
                <p>
                  Welcome to ParachPay ("we," "our," or "us"). We are committed to protecting your personal information 
                  and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard 
                your information when you use our payment gateway services.
              </p>
              <p>
                By using ParachPay, you agree to the collection and use of information in accordance with this policy. 
                If you do not agree with our policies and practices, please do not use our services.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="w-5 h-5 text-emerald-600" />
                Information We Collect
              </CardTitle>
            </CardHeader>
            <CardContent className="prose dark:prose-invert max-w-none">
              <h4>Personal Information</h4>
              <ul>
                <li><strong>Account Information:</strong> Name, email address, phone number, and password when you create an account</li>
                <li><strong>Identity Verification:</strong> Government-issued ID, date of birth, and address for compliance purposes</li>
                <li><strong>Financial Information:</strong> Bank account details, card information, and transaction history</li>
                <li><strong>Profile Information:</strong> Profile picture, preferences, and settings</li>
              </ul>
              
              <h4>Automatically Collected Information</h4>
              <ul>
                <li><strong>Device Information:</strong> IP address, browser type, operating system, and device identifiers</li>
                <li><strong>Usage Data:</strong> Pages visited, features used, and interaction patterns</li>
                <li><strong>Location Data:</strong> General geographic location based on IP address</li>
                <li><strong>Cookies:</strong> Session cookies and preference cookies for functionality</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="w-5 h-5 text-emerald-600" />
                How We Use Your Information
              </CardTitle>
            </CardHeader>
            <CardContent className="prose dark:prose-invert max-w-none">
              <p>We use the information we collect to:</p>
              <ul>
                <li>Process and complete your financial transactions</li>
                <li>Verify your identity and prevent fraud</li>
                <li>Comply with legal and regulatory requirements (KYC/AML)</li>
                <li>Send you transaction confirmations and account alerts</li>
                <li>Provide customer support and respond to inquiries</li>
                <li>Improve our services and develop new features</li>
                <li>Send promotional communications (with your consent)</li>
                <li>Analyze usage patterns to enhance user experience</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-emerald-600" />
                Information Sharing and Disclosure
              </CardTitle>
            </CardHeader>
            <CardContent className="prose dark:prose-invert max-w-none">
              <p>We may share your information with:</p>
              <ul>
                <li><strong>Payment Processors:</strong> To process transactions (e.g., Stripe, Mastercard)</li>
                <li><strong>Financial Institutions:</strong> Banks and card networks to complete transfers</li>
                <li><strong>Regulatory Authorities:</strong> When required by law or to comply with regulations</li>
                <li><strong>Service Providers:</strong> Third parties who assist in operating our services</li>
                <li><strong>Business Partners:</strong> With your consent, for joint services or promotions</li>
                <li><strong>WhatsApp Business Platform:</strong> If you choose to receive WhatsApp notifications, we use your WhatsApp number only to send you payment updates through the WhatsApp Business Platform provided by Meta. You can opt out anytime in Settings.</li>
              </ul>
              <p>
                We do <strong>NOT</strong> sell your personal information to third parties for marketing purposes.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-emerald-600" />
                Data Security
              </CardTitle>
            </CardHeader>
            <CardContent className="prose dark:prose-invert max-w-none">
              <p>We implement industry-standard security measures including:</p>
              <ul>
                <li>256-bit SSL/TLS encryption for all data transmission</li>
                <li>Two-factor authentication (2FA) for account access</li>
                <li>PCI DSS compliance for payment card data</li>
                <li>Regular security audits and penetration testing</li>
                <li>Encrypted data storage with access controls</li>
                <li>Real-time fraud monitoring and detection</li>
              </ul>
              <p>
                While we strive to protect your information, no method of transmission over the Internet is 100% secure. 
                We cannot guarantee absolute security but commit to promptly addressing any security incidents.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Your Rights and Choices</CardTitle>
            </CardHeader>
            <CardContent className="prose dark:prose-invert max-w-none">
              <p>You have the right to:</p>
              <ul>
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your data (subject to legal retention requirements)</li>
                <li><strong>Portability:</strong> Receive your data in a machine-readable format</li>
                <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
                <li><strong>Withdraw Consent:</strong> Revoke previously given consent</li>
              </ul>
              <p>
                To exercise these rights, please contact us at privacy@parachpay.com.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Data Retention</CardTitle>
            </CardHeader>
            <CardContent className="prose dark:prose-invert max-w-none">
              <p>
                We retain your personal information for as long as necessary to provide our services and comply with 
                legal obligations. Financial transaction records are retained for a minimum of 7 years as required 
                by financial regulations. You may request deletion of non-essential data at any time.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-emerald-600" />
                Contact Us
              </CardTitle>
            </CardHeader>
            <CardContent className="prose dark:prose-invert max-w-none">
              <p>
                If you have questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <ul>
                <li><strong>Email:</strong> privacy@parachpay.com</li>
                <li><strong>Support:</strong> support@parachpay.com</li>
              </ul>
              <p>
                We will respond to your inquiry within 30 days.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Changes to This Policy</CardTitle>
            </CardHeader>
            <CardContent className="prose dark:prose-invert max-w-none">
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any material changes by 
                posting the new policy on this page and updating the "Last updated" date. We encourage you to review 
                this Privacy Policy periodically for any changes.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-border text-center text-sm text-muted-foreground">
          <p>© 2026 ParachPay. All rights reserved.</p>
          <div className="flex justify-center gap-4 mt-2">
            <Link to="/privacy" className="hover:text-emerald-600 transition-colors">Privacy Policy</Link>
            <span>•</span>
            <Link to="/terms" className="hover:text-emerald-600 transition-colors">Terms & Conditions</Link>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default PrivacyPolicy;
